import { useState, useCallback } from 'react';
import { parseN8NResponse } from '../../../utils/parseN8NResponse';
import { useAuth } from '../../../context/AuthContext';
import { supabase } from '../../../supabaseClient';
import { track } from '../../../lib/analytics';

/**
 * Hook to manage CV auto-fill via N8N webhook.
 */

// Production N8N Webhook URL
const N8N_WEBHOOK_URL = 'https://maxbconnect.duckdns.org/webhook/4665c493-dbec-448c-b586-e8bfed71cd3b';

export const useCVAutoFill = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [parsedData, setParsedData] = useState(null);
    const { user } = useAuth();

    /**
     * Simulates parsing with mock data (matched to N8N flat structure).
     */
    const triggerMockParsing = useCallback(async () => {
        console.log("🛠️ Mode Démo : Utilisation des données Mock (Structure Plate)");
        setIsLoading(true);
        setError(null);

        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 1500));

        // MOCK DATA matching the N8N Flat structure provided by user
        const mockResponse = [
            {
                "passeport_semantique": "PROFILE: Transaction Services ...",
                "Intitulé_poste_1": "Transaction Services & Restructuring Senior",
                "employeur_1": "Eight Advisory",
                "localisation_poste_1": "Paris, France",
                "type_contrat_1": "CDI",
                "Date_debut_poste_1": "09/2021",
                "Date_fin_poste_1": "maitenant",
                "Description_missions_1": "Performing financial due diligence...",
                "Intitulé_poste_2": "Infrastructure Investments intern",
                "employeur_2": "Amundi Energy Transition",
                "Date_debut_poste_2": "03/2020",
                "Date_fin_poste_2": "08/2020",
                "Nom_ecole_1": "Sorbonne University",
                "Nom_formation_1": "MSc. in Financial Engineering",
                "Date_debut_formation_1": "2018",
                "Date_fin_formation_1": "2021",
                "Langue_1": "Français",
                "Niveau_langue_1": "Maternel",
                "Langue_2": "Anglais",
                "Niveau_langue_2": "Courant",
                "Competence_adhoc_1": "Excel",
                "Competence_adhoc_2": "VBA"
            }
        ];

        try {
            const normalized = parseN8NResponse(mockResponse);
            setParsedData(normalized);
            setIsLoading(false);
            return normalized;
        } catch (e) {
            console.error("Mock Parsing Error:", e);
            setError("Erreur interne lors du mock.");
            setIsLoading(false);
            throw e;
        }
    }, []);

    /**
     * Sends the PDF to N8N or Fallback to Mock
     */
    const triggerParsing = useCallback(async (pdfFile, currentCount) => {
        // Enforce Mock if URL is missing (but we hardcoded it now)
        if (!N8N_WEBHOOK_URL) {
            return triggerMockParsing();
        }

        setIsLoading(true);
        setError(null);
        setParsedData(null);

        try {
            const formData = new FormData();
            formData.append('file', pdfFile);
            formData.append('filename', pdfFile.name);

            // Fetch from N8N with Safari-friendly settings
            const response = await fetch(N8N_WEBHOOK_URL, {
                method: 'POST',
                body: formData,
                mode: 'cors', // Explicitly state CORS for Safari
                // Important: Do not set Content-Type manually when using FormData
                // The browser will automatically set it to multipart/form-data with the correct boundary
            });

            if (!response.ok) {
                throw new Error(`N8N Error: ${response.status} ${response.statusText}`);
            }

            const rawData = await response.json();
            const normalized = parseN8NResponse(rawData);

            // Update Supabase Profile with new data
            if (user) {
                try {
                    // Separate embedding from CV Data to avoid bloating the JSON
                    const { embedding, ...cleanCVData } = normalized;

                    // cv_data est remplacé en bloc ci-dessous : on doit explicitement
                    // reporter ai_generation_history (compteur de crédits IA), sinon un
                    // ré-upload de CV réinitialiserait silencieusement les 3 essais gratuits.
                    const { data: existingProfile } = await supabase
                        .from('profiles')
                        .select('cv_data')
                        .eq('id', user.id)
                        .single();

                    const updatePayload = {
                        cv_data: {
                            ...cleanCVData,
                            ai_generation_history: existingProfile?.cv_data?.ai_generation_history || [],
                        },
                        updated_at: new Date().toISOString(),
                        cv_upload_count: (currentCount || 0) + 1,
                        last_cv_update: new Date().toISOString(),
                        last_cv_data_update: new Date().toISOString(),
                        // Also update match stats since N8N returns embedding
                        last_match_update: new Date().toISOString(),
                        embedding: embedding || null // Ensure embedding is saved to its own column
                    };

                    console.log("Saving to Supabase:", updatePayload);
                    console.log("Embedding present?", !!normalized.embedding);

                    const { error: updateError } = await supabase
                        .from('profiles')
                        .update(updatePayload)
                        .eq('id', user.id);

                    if (updateError) {
                        console.error("Error updating profile from webhook:", updateError);
                    } else {
                        console.log("✅ Profile updated successfully from Webhook data");
                        track('cv_uploaded', { context: 'profile', upload_number: (currentCount || 0) + 1 });
                    }
                } catch (dbError) {
                    console.error("Database update failed:", dbError);
                }
            }

            setParsedData(normalized);
            setIsLoading(false);

            return normalized;

        } catch (err) {
            console.error('CV Parsing Error:', err);
            setError(err.message || 'Erreur lors de l\'analyse du CV');
            setIsLoading(false);
            throw err;
        }
    }, [user, triggerMockParsing]);

    const clearData = useCallback(() => {
        setParsedData(null);
        setError(null);
    }, []);

    return {
        triggerParsing,
        triggerMockParsing,
        clearData,
        isLoading,
        error,
        parsedData,
    };
};

export default useCVAutoFill;
